import React from 'react';
import { X } from 'lucide-react';
import { AlertFormData, SelectedBrand, SelectedSize, SelectedMaterial, SelectedPattern, SelectedLanguage, SelectedPlatform, SelectedRating } from '../types/alert.types';
import { getColorByTitle } from '../utils/vintedColors';

interface FilterBadgesSummaryProps {
  formData: AlertFormData;
  onRemoveCategory?: () => void;
  onRemoveCondition?: (condition: string) => void;
  onRemoveSize?: (size: SelectedSize) => void;
  onRemoveColor?: (color: string) => void;
  onRemoveBrand?: (brand: SelectedBrand) => void;
  onRemoveStatus?: (statusId: number) => void;
  onRemovePlatform?: (platform: SelectedPlatform) => void;
  onRemoveRating?: (rating: SelectedRating) => void;
  onRemoveMaterial?: (material: SelectedMaterial) => void;
  onRemovePattern?: (pattern: SelectedPattern) => void;
  onRemoveLanguage?: (language: SelectedLanguage) => void;
  onRemoveInclude?: (word: string) => void;
  onRemoveExclude?: (word: string) => void;
  onClearPrice?: () => void;
  onClearLikes?: () => void;
}

const statusLabels: Record<number, string> = {
  6: 'Neuf avec étiquette',
  1: 'Neuf sans étiquette',
  2: 'Très bon état',
  3: 'Bon état',
  4: 'Satisfaisant'
};

const platformLabels: Record<number, string> = {
  1281: 'PlayStation 5', 1280: 'PlayStation 4', 1279: 'PlayStation 3',
  1277: 'PlayStation 1', 1278: 'PlayStation 2', 1282: 'PlayStation Portable',
  1283: 'PlayStation Vita', 1291: 'Xbox Series S et X', 1290: 'Xbox One',
  1289: 'Xbox 360', 1288: 'Xbox (originale)', 1273: 'Nintendo Switch',
  6478: 'Nintendo Switch 2', 1274: 'Nintendo Wii', 1275: 'Nintendo Wii U',
  1272: 'Nintendo GameCube', 1265: 'Nintendo 3DS', 1264: 'Nintendo 2DS',
  1267: 'Nintendo DS', 1270: 'Nintendo Game Boy Advance', 1269: 'Nintendo Game Boy',
  1266: 'Nintendo 64', 1268: 'Nintendo Entertainment System', 1287: 'Super Nintendo',
  1276: 'PC et Mac', 1286: 'Steam Deck', 1284: 'Sega Dreamcast', 1285: 'Sega Mega Drive',
  1260: 'Atari', 1262: 'Commodore', 1259: 'ASUS ROG Ally', 1261: 'Ayaneo', 1263: 'Lenovo Legion Go'
};

const ratingLabels: Record<number, string> = {
  160: 'PEGI 3', 161: 'PEGI 7', 162: 'PEGI 12', 164: 'PEGI 16', 163: 'PEGI 18',
  170: 'E – Tous publics', 171: 'E10+ – 10 ans et plus', 172: 'T – 13 ans et plus',
  173: 'M – 17 ans et plus', 175: 'AO – Réservé aux adultes', 174: 'RP – En attente',
  176: 'Non précisé', 165: 'USK 0', 166: 'USK 6', 167: 'USK 12', 168: 'USK 16', 169: 'USK 18'
};

interface BadgeProps {
  emoji: string;
  label: string;
  bgColor: string;
  textColor: string;
  onRemove?: () => void;
  colorHex?: string;
}

const Badge: React.FC<BadgeProps> = ({ emoji, label, bgColor, textColor, onRemove, colorHex }) => (
  <span className={`inline-flex items-center gap-1.5 px-3 py-1 ${bgColor} ${textColor} rounded-lg text-sm font-medium transition-all`}>
    {colorHex && (
      <span
        className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0"
        style={{ backgroundColor: `#${colorHex}` }}
      />
    )}
    {emoji} {label}
    {onRemove && (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="ml-0.5 hover:opacity-70 transition-opacity"
      >
        <X size={12} />
      </button>
    )}
  </span>
);

export const FilterBadgesSummary: React.FC<FilterBadgesSummaryProps> = ({
  formData,
  onRemoveCategory,
  onRemoveCondition,
  onRemoveSize,
  onRemoveColor,
  onRemoveBrand,
  onRemoveStatus,
  onRemovePlatform,
  onRemoveRating,
  onRemoveMaterial,
  onRemovePattern,
  onRemoveLanguage,
  onRemoveInclude,
  onRemoveExclude,
  onClearPrice,
  onClearLikes
}) => {
  const badges: React.ReactNode[] = [];

  // Catégorie
  if (formData.categoryPath) {
    badges.push(
      <Badge key="category" emoji="📂" label={formData.categoryPath} bgColor="bg-purple-50" textColor="text-purple-700" onRemove={onRemoveCategory} />
    );
  }

  // Prix
  const minPrice = formData.minPrice;
  const maxPrice = formData.maxPrice;
  if (minPrice && maxPrice) {
    badges.push(
      <Badge key="price" emoji="💰" label={`${minPrice}€ - ${maxPrice}€`} bgColor="bg-blue-100" textColor="text-blue-700" onRemove={onClearPrice} />
    );
  } else if (minPrice) {
    badges.push(
      <Badge key="price" emoji="💰" label={`Min: ${minPrice}€`} bgColor="bg-blue-100" textColor="text-blue-700" onRemove={onClearPrice} />
    );
  } else if (maxPrice) {
    badges.push(
      <Badge key="price" emoji="💰" label={`Max: ${maxPrice}€`} bgColor="bg-blue-100" textColor="text-blue-700" onRemove={onClearPrice} />
    );
  }

  // Likes
  const minLikes = formData.minLikes;
  const maxLikes = formData.maxLikes;
  if (minLikes && maxLikes) {
    badges.push(
      <Badge key="likes" emoji="❤️" label={`${minLikes} - ${maxLikes} likes`} bgColor="bg-pink-100" textColor="text-pink-700" onRemove={onClearLikes} />
    );
  } else if (minLikes) {
    badges.push(
      <Badge key="likes" emoji="❤️" label={`Min ${minLikes} likes`} bgColor="bg-pink-100" textColor="text-pink-700" onRemove={onClearLikes} />
    );
  } else if (maxLikes) {
    badges.push(
      <Badge key="likes" emoji="❤️" label={`Max ${maxLikes} likes`} bgColor="bg-pink-100" textColor="text-pink-700" onRemove={onClearLikes} />
    );
  }

  // Trending
  if (formData.trendingOnly) {
    badges.push(
      <Badge key="trending" emoji="🔥" label="En demande" bgColor="bg-orange-100" textColor="text-orange-700" />
    );
  }

  // Conditions
  formData.selectedConditions.forEach((condition, idx) => {
    badges.push(
      <Badge
        key={`condition-${idx}`}
        emoji="💎"
        label={condition}
        bgColor="bg-green-100"
        textColor="text-green-700"
        onRemove={onRemoveCondition ? () => onRemoveCondition(condition) : undefined}
      />
    );
  });

  // Tailles
  formData.selectedSizes.forEach((size) => {
    badges.push(
      <Badge
        key={`size-${size.id}`}
        emoji="📏"
        label={size.title}
        bgColor="bg-indigo-100"
        textColor="text-indigo-700"
        onRemove={onRemoveSize ? () => onRemoveSize(size) : undefined}
      />
    );
  });

  // Couleurs - Badge condensé avec compteur
  if (formData.selectedColors && formData.selectedColors.length > 0) {
    const count = formData.selectedColors.length;
    const label = count === 1
      ? formData.selectedColors[0]
      : `${count} couleurs`;

    badges.push(
      <Badge
        key="colors"
        emoji="🎨"
        label={label}
        bgColor="bg-purple-100"
        textColor="text-purple-700"
      />
    );
  }

  // Marques
  formData.selectedBrands.forEach((brand) => {
    badges.push(
      <Badge
        key={`brand-${brand.id}`}
        emoji="🏷️"
        label={brand.title}
        bgColor="bg-yellow-100"
        textColor="text-yellow-700"
        onRemove={onRemoveBrand ? () => onRemoveBrand(brand) : undefined}
      />
    );
  });

  // États (statuts) - Badge condensé avec compteur - icône diamant 💎
  if (formData.selectedStatuses && formData.selectedStatuses.length > 0) {
    const count = formData.selectedStatuses.length;
    const label = count === 1
      ? statusLabels[formData.selectedStatuses[0]] || `1 état`
      : `${count} états`;

    badges.push(
      <Badge
        key="statuses"
        emoji="💎"
        label={label}
        bgColor="bg-blue-100"
        textColor="text-blue-700"
      />
    );
  }

  // Plateformes
  (formData.selectedPlatforms || []).forEach((platform) => {
    badges.push(
      <Badge
        key={`platform-${platform.id}`}
        emoji="🎮"
        label={platform.title}
        bgColor="bg-cyan-100"
        textColor="text-cyan-700"
        onRemove={onRemovePlatform ? () => onRemovePlatform(platform) : undefined}
      />
    );
  });

  // Classifications
  (formData.selectedRatings || []).forEach((rating) => {
    badges.push(
      <Badge
        key={`rating-${rating.id}`}
        emoji="🎮"
        label={rating.title}
        bgColor="bg-red-100"
        textColor="text-red-700"
        onRemove={onRemoveRating ? () => onRemoveRating(rating) : undefined}
      />
    );
  });

  // Matières - Badge condensé avec compteur
  if (formData.selectedMaterials && formData.selectedMaterials.length > 0) {
    const count = formData.selectedMaterials.length;
    const label = count === 1
      ? formData.selectedMaterials[0].title
      : `${count} matières`;

    badges.push(
      <Badge
        key="materials"
        emoji="👕"
        label={label}
        bgColor="bg-amber-100"
        textColor="text-amber-700"
      />
    );
  }

  // Motifs - Badge condensé avec compteur
  if (formData.selectedPatterns && formData.selectedPatterns.length > 0) {
    const count = formData.selectedPatterns.length;
    const label = count === 1
      ? formData.selectedPatterns[0].title
      : `${count} motifs`;

    badges.push(
      <Badge
        key="patterns"
        emoji="🎨"
        label={label}
        bgColor="bg-fuchsia-100"
        textColor="text-fuchsia-700"
      />
    );
  }

  // Langues
  (formData.selectedLanguages || []).forEach((language) => {
    badges.push(
      <Badge
        key={`language-${language.id}`}
        emoji="🌍"
        label={language.title}
        bgColor="bg-emerald-100"
        textColor="text-emerald-700"
        onRemove={onRemoveLanguage ? () => onRemoveLanguage(language) : undefined}
      />
    );
  });

  // Mots à inclure
  formData.mustInclude.forEach((word, idx) => {
    badges.push(
      <Badge
        key={`include-${idx}`}
        emoji="✅"
        label={word}
        bgColor="bg-teal-100"
        textColor="text-teal-700"
        onRemove={onRemoveInclude ? () => onRemoveInclude(word) : undefined}
      />
    );
  });

  // Mots à exclure
  formData.mustExclude.forEach((word, idx) => {
    badges.push(
      <Badge
        key={`exclude-${idx}`}
        emoji="🚫"
        label={word}
        bgColor="bg-red-100"
        textColor="text-red-700"
        onRemove={onRemoveExclude ? () => onRemoveExclude(word) : undefined}
      />
    );
  });

  if (badges.length === 0) return null;

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Filtres actifs</p>
      <div className="flex flex-wrap gap-2">
        {badges}
      </div>
    </div>
  );
};
